// frontend/src/admin/v2/hooks/useIntegrations.js
import { useEffect, useState, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

/**
 * useIntegrations — fetches /api/admin/integrations once on mount and
 * exposes a probe(key) helper that calls the live-probe endpoint and
 * splices the returned payload into the local list.
 *
 * AbortController cancels inflight on unmount.
 *
 * Returns { data, loading, error, errorTitle, probe, refetch }.
 *   data = { integrations: [...], summary: {total, green, amber, red} } | null
 *   probe(key) = async (key) → updated integration entry, also merges into data
 *
 * 🔴 Welle 3 PR 4 (§4.1): `error` ist der Nutzersatz, `errorTitle` traegt das
 * Entwicklerdetail (den Env-Namen) fuer den Tooltip.
 */
export function useIntegrations() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorTitle, setErrorTitle] = useState(null)
  const inflightRef = useRef(null)

  const fetchAll = useCallback(async () => {
    if (inflightRef.current) inflightRef.current.abort()
    const c = new AbortController()
    inflightRef.current = c
    try {
      const r = await axios.get(`${API}/api/admin/integrations`, {
        headers: HEADERS,
        signal: c.signal,
      })
      if (inflightRef.current !== c) return
      setData(r.data)
      setError(null)
      setErrorTitle(null)
    } catch (e) {
      if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return
      const isKey = e.response?.status === 403
      setError(isKey ? 'Admin data is unavailable in this build.' : (e.message || 'Failed to load'))
      setErrorTitle(isKey ? 'REACT_APP_ADMIN_KEY' : null)
    } finally {
      if (inflightRef.current === c) {
        setLoading(false)
        inflightRef.current = null
      }
    }
  }, [])

  const probe = useCallback(async (key) => {
    try {
      const r = await axios.post(`${API}/api/admin/integrations/${key}/health`, null, {
        headers: HEADERS,
      })
      const updated = r.data
      // Splice the updated integration into the existing list. Re-derive
      // the summary so the header counters stay accurate.
      setData(prev => {
        if (!prev) return prev
        const integrations = prev.integrations.map(it => it.key === key ? updated : it)
        const summary = {
          total: integrations.length,
          green: integrations.filter(it => it.status === 'green').length,
          amber: integrations.filter(it => it.status === 'amber').length,
          red:   integrations.filter(it => it.status === 'red').length,
        }
        return { integrations, summary }
      })
      return updated
    } catch (e) {
      // Surface the error inline; caller decides how to render.
      throw e
    }
  }, [])

  useEffect(() => {
    fetchAll()
    return () => {
      if (inflightRef.current) {
        inflightRef.current.abort()
        inflightRef.current = null
      }
    }
  }, [fetchAll])

  return { data, loading, error, errorTitle, probe, refetch: fetchAll }
}
